import {Box, Grid, Typography} from '@mui/material';
import React, {useEffect, useRef, useState} from 'react';
import AurUserCreatorDetails from '../../user-management/aur-user-details-view/AurUserCreatorDetails';
import AurClientListProperties from './AurClientListProperties';
import AurAccordion, {
  AurAccordionRef,
} from '../../common/aur-accordion/AurAccordion';
import AurIconButton from '../../common/aur-button/AurIconButton';
import AurIcon, {IconNames} from '../../common/aur-icon/AurIcon';
import StackedWorkspaces from '../../user-management/add-new-team-member/StackedWorkspaces';
import {ProjectDetails} from '../../pages/user-management/models';
import {t} from 'i18next';
import styles from '../../../styles/components/userManagement/clientInfoTab.module.scss';
import {IClientDetail} from '../../../types/client-management/cientManagementTypes';
import AurHighlyConfidential from '../../common/aur-highly-confidential/AurHighlyConfidential';
import HostCountryDetails from '../../aur-host-country-details/AurHostCountryDetails';
import AurButton from '../../common/aur-button/AurButton';
import {ItemType} from '../../pages/user-management/tabs/AddClientInfo';
import {useSelector} from 'react-redux';
import {permissionGroupSelector} from '../../../redux/slices/permissionGroupSlice';
import {resolvePermissionGroupItemType} from '../../../utils/permissionGroupItemUtils';
import PermissionGroupDialog from '../../permission-groups/PermissionGroupDialog';
import {RootState} from '../../../redux/store';
import {PERMISSION_GROUP_CATEGORY} from '../../../constants/constants';

interface IProps {
  clientDetail: IClientDetail;
  toggleItem: (key: number) => void;
  setActiveStep?: ((value: number) => void | undefined) | undefined;
  flowName: string;
  projects?: ProjectDetails[];
  onEdit: () => void;
  onDelete: () => void;
  activeStep?: number;
}

const AurClientListView: React.FC<IProps> = (props: IProps) => {
  const {clientDetail, projects, activeStep = 0, onEdit, onDelete} = props;
  const accordionRef = useRef<AurAccordionRef>(null);
  const [selectedPermissionGroupDetails, setSelectedPermissionGroupDetails] =
    useState<ItemType>();
  const [permCategory, setPermCategory] = useState(
    PERMISSION_GROUP_CATEGORY.CLIENT
  );
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const permissionGroupsState = useSelector(permissionGroupSelector);

  const teamMemberPermissionInfo = useSelector(
    (state: RootState) => state.teamMember.teamMemberPermissionInfo
  );

  useEffect(() => {
    if (clientDetail.isExpanded) {
      accordionRef?.current?.expand();
    } else {
      accordionRef?.current?.collapse();
    }
  }, [clientDetail.isExpanded]);

  const titleContent = (
    <>
      <Grid item xs="auto">
        <AurIconButton
          size="big"
          shape
          icon={<AurIcon icon={IconNames.Remove} />}
          onClick={() => onDelete()}
          dataTestId="delete-icon-button"
          ariaLabel={t('buttons.delete')}
        />
      </Grid>
      <Grid item xs="auto" className="client-list-icon">
        <AurIconButton
          size="big"
          shape
          icon={<AurIcon icon={IconNames.Edit} />}
          onClick={() => onEdit()}
          dataTestId="edit-icon-button"
          ariaLabel={t('buttons.edit')}
        />
      </Grid>
    </>
  );

  const creatorDetail = (
    <>
      {activeStep == 2 ? (
        <></>
      ) : (
        <Box className="client-list-properties ">
          <AurUserCreatorDetails
            createdBy={clientDetail.createdBy}
            updatedBy={clientDetail.updatedBy}
            createdDate={clientDetail.createdDate.toLocaleDateString('en-ZA')}
            updatedDate={clientDetail.updatedDate.toLocaleDateString('en-ZA')}
          />
        </Box>
      )}
    </>
  );

  const openPermissionGroupDetails = (
    permissionGroupId: string | undefined,
    category: PERMISSION_GROUP_CATEGORY
  ) => {
    setSelectedPermissionGroupDetails(
      resolvePermissionGroupItemType(
        permissionGroupId,
        category == PERMISSION_GROUP_CATEGORY.CLIENT
          ? teamMemberPermissionInfo?.clientPermList
          : teamMemberPermissionInfo?.projPermList,
        permissionGroupsState?.permissionGroups
      )
    );
    setPermCategory(category);
    setShowPermissionModal(true);
  };

  const getPermissionGroupDetails = (permissionGroupId: string | undefined) => {
    const permGroup = teamMemberPermissionInfo?.clientPermList?.find(
      (item) => item.ItemKey == permissionGroupId
    );
    return {
      name: permGroup?.ItemName,
      description: permGroup?.ItemDescription,
    };
  };

  const bodyContent = (
    <>
      {!clientDetail.isAdmin &&
        clientDetail.clientListProperties.map((prop) => {
          if (
            prop.key == t('addEditTeamMember.form.clientPermissionGroupLabel')
          ) {
            prop.clientPermInfo = getPermissionGroupDetails(prop.value);
          }
          return (
            <AurClientListProperties
              property={prop}
              showPermDeatils={(id) => {
                openPermissionGroupDetails(
                  id,
                  PERMISSION_GROUP_CATEGORY.CLIENT
                );
              }}
            />
          );
        })}
      {(!clientDetail.isAdmin || (projects && projects.length > 0)) && (
        <Grid className="client-list-properties" container>
          <Grid item xs={2} className="client-list-properties-key">
            <Typography className="typo-text subhead3_size">
              {t('addEditTeamMember.form.projectLabel')}
            </Typography>
          </Grid>
          <Grid item xs className="client-list-properties-value">
            {projects && projects.length > 0
              ? projects?.map((item) => {
                  return (
                    <Box
                      sx={{paddingBottom: '48px'}}
                      className="project-properties"
                      key={item.projectId}
                    >
                      <Box className={`${styles.workspaceContainerHeader}`}>
                        {item.projectName}
                      </Box>
                      {item.highlyConfidential ? (
                        <AurHighlyConfidential
                          size="small"
                          className="client-list-highly-confidential"
                          type="pill"
                          showWarningText={true}
                        />
                      ) : (
                        <></>
                      )}

                      <HostCountryDetails {...item} />

                      {!clientDetail.isAdmin && (
                        <StackedWorkspaces
                          workspaces={item.workspaces}
                          projectPermissions={item.projectPermissions}
                        />
                      )}
                      {!clientDetail.isAdmin && (
                        <Box>
                          <Typography className="typo-text subhead4_size heading-color">
                            {item?.permissionGroup}
                          </Typography>
                          {item?.permissionGroupId && (
                            <AurButton
                              ariaLabel={t(
                                'addEditTeamMember.form.permissionGroupDetails'
                              )}
                              disabled={false}
                              onClick={() =>
                                openPermissionGroupDetails(
                                  item.permissionGroupId,
                                  PERMISSION_GROUP_CATEGORY.PROJECT
                                )
                              }
                              type="text"
                              size="Small"
                              label={t(
                                'addEditTeamMember.form.permissionGroupDetails'
                              )}
                              classNames="remove-padding"
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })
              : !clientDetail.isAdmin && (
                  <Typography
                    className={`typo-text body3_size no-project-text`}
                  >
                    {t('addEditTeamMember.form.noProjectsAssigned')}
                  </Typography>
                )}
          </Grid>
        </Grid>
      )}
    </>
  );

  const adminRoleInfo = (
    <>
      <AurClientListProperties
        property={{
          key: t('UserProfile.roleLabel'),
          value: t('addEditTeamMember.form.clientAdminLabel'),
        }}
      />
      <Grid container sm={8} md={8}>
        <Box className={`${styles.reviewTabAdminText}`}>
          <Typography className="client-info-hidden-note-text">
            {t('addEditTeamMember.form.clientAdminContentAccess')}
            <strong>{t('addEditTeamMember.form.projectAndWorkspaces')}</strong>
            {t('addEditTeamMember.form.withClientinAurora')}
          </Typography>
        </Box>
      </Grid>
    </>
  );

  return (
    <AurAccordion
      fullWidth
      title={clientDetail.clientName}
      titleContent={titleContent}
      ref={accordionRef}
      alertWarningIcon={false}
      className="client-details"
    >
      {clientDetail.isAdmin ? adminRoleInfo : <></>}
      {bodyContent}
      {creatorDetail}
      {showPermissionModal ? (
        <PermissionGroupDialog
          open={showPermissionModal}
          onClose={() => setShowPermissionModal(false)}
          permissionModalData={selectedPermissionGroupDetails}
          permCategory={permCategory}
        />
      ) : (
        <></>
      )}
    </AurAccordion>
  );
};

export default AurClientListView;
